import { IconButton, Menu } from '@affine/component';
import { useAppSettingHelper } from '@affine/core/components/hooks/affine/use-app-setting-helper';
import {
  AddPageButton,
  AppDownloadButton,
  AppSidebar,
  MenuItem,
  MenuLinkItem,
  QuickSearchInput,
  SidebarContainer,
  SidebarScrollableContainer,
} from '@affine/core/modules/app-sidebar/views';
import { AuthService, ServerService } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { CMDKQuickSearchService } from '@affine/core/modules/quicksearch/services/cmdk';
import type { Workspace } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import type { Store } from '@blocksuite/affine/store';
import {
  AiOutlineIcon,
  AllDocsIcon,
  ArrowDownSmallIcon,
  ImportIcon,
  SearchIcon,
  SettingsIcon,
} from '@blocksuite/icons/rc';
import {
  resolveSidebarMode,
  useLiveData,
  useService,
  useServices,
} from '@toeverything/infra';
import type { ReactElement } from 'react';
import { memo, useCallback } from 'react';

import { NavigationPanelOrganize } from '../../desktop/components/navigation-panel';
import { WorkbenchLink, WorkbenchService } from '../../modules/workbench';
import { WorkspaceNavigator } from '../workspace-selector';
import { InstanceBrand } from '../instance-branding';
import {
  bottomContainer,
  compactHeader,
  compactOverflowBottom,
  compactOverflowContent,
  compactOverflowNewPage,
  compactOverflowTrigger,
  compactToolbar,
  compactToolbarButton,
  compactToolbarLink,
  navigationPanelContent,
  quickSearch,
  quickSearchAndNewPage,
  workspaceAndUserWrapper,
  workspaceWrapper,
} from './index.css';
import { InviteMembersButton } from './invite-members-button';
import { AppSidebarJournalButton } from './journal-button';
import { NotificationButton } from './notification-button';
import { SidebarAudioPlayer } from './sidebar-audio-player';
import { TemplateDocEntrance } from './template-doc-entrance';
import { TrashButton } from './trash-button';
import { UpdaterButton } from './updater-button';
import UserInfo from './user-info';

export type RootAppSidebarProps = {
  isPublicWorkspace: boolean;
  onOpenQuickSearchModal: () => void;
  onOpenSettingModal: () => void;
  currentWorkspace: Workspace;
  openPage: (pageId: string) => void;
  createPage: () => Store;
  paths: {
    all: (workspaceId: string) => string;
    trash: (workspaceId: string) => string;
    shared: (workspaceId: string) => string;
  };
};

const AllDocsButton = () => {
  const t = useI18n();
  const { workbenchService } = useServices({
    WorkbenchService,
  });
  const workbench = workbenchService.workbench;
  const allPageActive = useLiveData(
    workbench.location$.selector(location => location.pathname === '/all')
  );

  return (
    <MenuLinkItem icon={<AllDocsIcon />} active={allPageActive} to={'/all'}>
      <span data-testid="all-pages">
        {t['com.affine.workspaceSubPath.all']()}
      </span>
    </MenuLinkItem>
  );
};

const AIChatButton = () => {
  const t = useI18n();
  const featureFlagService = useService(FeatureFlagService);
  const serverService = useService(ServerService);
  const serverFeatures = useLiveData(serverService.server.features$);
  const enableAI = useLiveData(featureFlagService.flags.enable_ai.$);

  const { workbenchService } = useServices({
    WorkbenchService,
  });
  const workbench = workbenchService.workbench;
  const aiChatActive = useLiveData(
    workbench.location$.selector(location => location.pathname === '/chat')
  );

  if (!enableAI || !serverFeatures?.copilot) {
    return null;
  }

  return (
    <MenuLinkItem icon={<AiOutlineIcon />} active={aiChatActive} to={'/chat'}>
      <span data-testid="ai-chat">
        {t['com.affine.workspaceSubPath.chat']()}
      </span>
    </MenuLinkItem>
  );
};

const CompactAllDocsButton = () => {
  const t = useI18n();
  const { workbenchService } = useServices({
    WorkbenchService,
  });
  const workbench = workbenchService.workbench;
  const allPageActive = useLiveData(
    workbench.location$.selector(location => location.pathname === '/all')
  );

  return (
    <WorkbenchLink
      aria-label={t['com.affine.workspaceSubPath.all']()}
      className={compactToolbarLink}
      draggable={false}
      to={'/all'}
    >
      <IconButton
        aria-label={t['com.affine.workspaceSubPath.all']()}
        className={compactToolbarButton}
        data-active={allPageActive ? 'true' : 'false'}
        data-testid="compact-sidebar-all-docs"
        size={20}
        tooltip={t['com.affine.workspaceSubPath.all']()}
      >
        <AllDocsIcon />
      </IconButton>
    </WorkbenchLink>
  );
};

type SidebarLayoutProps = {
  onOpenImportModal: () => void;
  onOpenQuickSearchModal: () => void;
  onOpenSettingModal: () => void;
  onWorkspaceSelectorOpenChange: (open: boolean) => void;
  showDownloadApp: boolean;
  showNotifications: boolean;
  workspaceSelectorOpen: boolean;
};

const CompactOverflowContent = ({
  onOpenImportModal,
  showDownloadApp,
}: Pick<SidebarLayoutProps, 'onOpenImportModal' | 'showDownloadApp'>) => {
  const t = useI18n();

  return (
    <div className={compactOverflowContent} data-testid="compact-sidebar-menu">
      <div className={compactOverflowNewPage}>
        <span>{t['New Page']()}</span>
        <AddPageButton />
      </div>
      <AppSidebarJournalButton />
      <MenuItem
        data-testid="compact-sidebar-import-button"
        icon={<ImportIcon />}
        onClick={onOpenImportModal}
      >
        {t['Import']()}
      </MenuItem>
      <TrashButton />
      <InviteMembersButton />
      <TemplateDocEntrance />
      <AIChatButton />
      <div className={compactOverflowBottom}>
        <SidebarAudioPlayer />
        {BUILD_CONFIG.isElectron ? (
          <UpdaterButton />
        ) : showDownloadApp ? (
          <AppDownloadButton />
        ) : null}
      </div>
    </div>
  );
};

export const CompactSidebar = ({
  onOpenImportModal,
  onOpenQuickSearchModal,
  onOpenSettingModal,
  onWorkspaceSelectorOpenChange,
  showDownloadApp,
  showNotifications,
  workspaceSelectorOpen,
}: SidebarLayoutProps) => {
  const t = useI18n();

  return (
    <>
      <SidebarContainer className={compactHeader}>
        {BUILD_CONFIG.isElectron ? <InstanceBrand /> : null}
        <div className={workspaceAndUserWrapper}>
          <div className={workspaceWrapper}>
            <WorkspaceNavigator
              showDiscoverAction={false}
              showEnableCloudButton
              showSyncStatus
              open={workspaceSelectorOpen}
              onOpenChange={onWorkspaceSelectorOpenChange}
              dense
            />
          </div>
          <UserInfo />
        </div>
        <div className={compactToolbar} data-testid="compact-sidebar-toolbar">
          <CompactAllDocsButton />
          {showNotifications ? (
            <NotificationButton compact className={compactToolbarButton} />
          ) : null}
          <IconButton
            aria-label={t['Search']()}
            className={compactToolbarButton}
            data-event-props="$.navigationPanel.$.quickSearch"
            data-testid="compact-sidebar-search"
            size={20}
            tooltip={t['Search']()}
            onClick={onOpenQuickSearchModal}
          >
            <SearchIcon />
          </IconButton>
          <IconButton
            aria-label={t['com.affine.settingSidebar.title']()}
            className={compactToolbarButton}
            data-testid="compact-sidebar-settings"
            size={20}
            tooltip={t['com.affine.settingSidebar.title']()}
            onClick={onOpenSettingModal}
          >
            <SettingsIcon />
          </IconButton>
          <Menu
            contentOptions={{ side: 'bottom', align: 'end', sideOffset: 8 }}
            items={
              <CompactOverflowContent
                onOpenImportModal={onOpenImportModal}
                showDownloadApp={showDownloadApp}
              />
            }
          >
            <IconButton
              aria-label={t['com.affine.rootAppSidebar.more']()}
              className={`${compactToolbarButton} ${compactOverflowTrigger}`}
              data-testid="compact-sidebar-overflow"
              size={20}
              tooltip={t['com.affine.rootAppSidebar.more']()}
            >
              <ArrowDownSmallIcon />
            </IconButton>
          </Menu>
        </div>
      </SidebarContainer>
      <SidebarScrollableContainer
        contentClassName={navigationPanelContent}
        contentLayout="accordion"
      >
        <NavigationPanelOrganize />
      </SidebarScrollableContainer>
    </>
  );
};

const AffineSidebar = ({
  onOpenImportModal,
  onOpenQuickSearchModal,
  onOpenSettingModal,
  onWorkspaceSelectorOpenChange,
  showDownloadApp,
  showNotifications,
  workspaceSelectorOpen,
}: SidebarLayoutProps) => {
  const t = useI18n();

  return (
    <>
      <SidebarContainer>
        {BUILD_CONFIG.isElectron ? <InstanceBrand /> : null}
        <div className={workspaceAndUserWrapper}>
          <div className={workspaceWrapper}>
            <WorkspaceNavigator
              showDiscoverAction={false}
              showEnableCloudButton
              showSyncStatus
              open={workspaceSelectorOpen}
              onOpenChange={onWorkspaceSelectorOpenChange}
              dense
            />
          </div>
          <UserInfo />
        </div>
        <div className={quickSearchAndNewPage}>
          <QuickSearchInput
            className={quickSearch}
            data-testid="slider-bar-quick-search-button"
            data-event-props="$.navigationPanel.$.quickSearch"
            onClick={onOpenQuickSearchModal}
          />
          <AddPageButton />
        </div>
        <AllDocsButton />
        {showNotifications && <NotificationButton />}
        <AppSidebarJournalButton />
        <MenuItem
          data-testid="slider-bar-import-button"
          icon={<ImportIcon />}
          onClick={onOpenImportModal}
        >
          <span data-testid="import-modal-trigger">{t['Import']()}</span>
        </MenuItem>
        <TrashButton />
        <InviteMembersButton />
        <TemplateDocEntrance />
        <AIChatButton />
        <MenuItem
          data-testid="slider-bar-workspace-setting-button"
          icon={<SettingsIcon />}
          onClick={onOpenSettingModal}
        >
          <span data-testid="settings-modal-trigger">
            {t['com.affine.settingSidebar.title']()}
          </span>
        </MenuItem>
      </SidebarContainer>
      <SidebarScrollableContainer
        contentClassName={navigationPanelContent}
        contentLayout="accordion"
      >
        <NavigationPanelOrganize />
      </SidebarScrollableContainer>
      <SidebarContainer className={bottomContainer}>
        <SidebarAudioPlayer />
        {BUILD_CONFIG.isElectron ? (
          <UpdaterButton />
        ) : showDownloadApp ? (
          <AppDownloadButton />
        ) : null}
      </SidebarContainer>
    </>
  );
};

/**
 * This is for the whole affine app sidebar.
 * This component wraps the app sidebar in `@affine/component` with logic and data.
 *
 */
export const RootAppSidebar = memo((): ReactElement => {
  const { workbenchService, cMDKQuickSearchService, authService } = useServices(
    {
      WorkbenchService,
      CMDKQuickSearchService,
      AuthService,
    }
  );

  const sessionStatus = useLiveData(authService.session.status$);
  const serverService = useService(ServerService);
  const serverConfig = useLiveData(serverService.server.config$);
  const { appSettings } = useAppSettingHelper();
  const workspaceDialogService = useService(WorkspaceDialogService);
  const workbench = workbenchService.workbench;
  const workspaceSelectorOpen = useLiveData(workbench.workspaceSelectorOpen$);
  const onOpenQuickSearchModal = useCallback(() => {
    cMDKQuickSearchService.toggle();
  }, [cMDKQuickSearchService]);

  const onWorkspaceSelectorOpenChange = useCallback(
    (open: boolean) => {
      workbench.setWorkspaceSelectorOpen(open);
    },
    [workbench]
  );

  const onOpenSettingModal = useCallback(() => {
    workspaceDialogService.open('setting', {
      activeTab: 'appearance',
    });
    track.$.navigationPanel.$.openSettings();
  }, [workspaceDialogService]);

  const handleOpenDocs = useCallback(
    (result: {
      docIds: string[];
      entryId?: string;
      isWorkspaceFile?: boolean;
    }) => {
      const { docIds, entryId, isWorkspaceFile } = result;
      // If the imported file is a workspace file, open the entry page.
      if (isWorkspaceFile && entryId) {
        workbench.openDoc(entryId);
      } else if (!docIds.length) {
        return;
      }
      // Open all the docs when there are multiple docs imported.
      if (docIds.length > 1) {
        workbench.openAll();
      } else {
        // Otherwise, open the only doc.
        workbench.openDoc(docIds[0]);
      }
    },
    [workbench]
  );

  const onOpenImportModal = useCallback(() => {
    track.$.navigationPanel.importModal.open();
    workspaceDialogService.open('import', undefined, payload => {
      if (!payload) {
        return;
      }
      handleOpenDocs(payload);
    });
  }, [workspaceDialogService, handleOpenDocs]);

  const sidebarProps: SidebarLayoutProps = {
    onOpenImportModal,
    onOpenQuickSearchModal,
    onOpenSettingModal,
    onWorkspaceSelectorOpenChange,
    showDownloadApp: serverConfig?.showDownloadApp ?? true,
    showNotifications: sessionStatus === 'authenticated',
    workspaceSelectorOpen: !!workspaceSelectorOpen,
  };

  return (
    <AppSidebar>
      {resolveSidebarMode(appSettings.sidebarMode) === 'affine' ? (
        <AffineSidebar {...sidebarProps} />
      ) : (
        <CompactSidebar {...sidebarProps} />
      )}
    </AppSidebar>
  );
});

RootAppSidebar.displayName = 'memo(RootAppSidebar)';
